/**
 * Protected Route Component
 * 
 * Provides role-based access control for protected routes.
 * - Checks authentication status
 * - Validates user role against allowedRoles
 * - Handles loading states during authentication checks
 * - Redirects appropriately based on auth/authorization status
 * 
 * Props:
 * - children: React components to render if authorized
 * - allowedRoles: Array of role strings that are allowed to access the route
 *                 e.g., ['SuperAdmin'], ['Staff', 'SuperAdmin']
 * - fallbackPath: Optional path to redirect to (defaults to '/' for unauthorized)
 * 
 * Usage:
 * <ProtectedRoute allowedRoles={['SuperAdmin']}>
 *   <AdminComponent />
 * </ProtectedRoute>
 * 
 * <ProtectedRoute allowedRoles={['Staff', 'SuperAdmin']}>
 *   <StaffOrAdminComponent />
 * </ProtectedRoute>
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

/**
 * Loading spinner component displayed during authentication check
 */
function LoadingSpinner() {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: '#f5f5f5',
        }}>
            <div style={{
                textAlign: 'center',
                padding: '20px',
            }}>
                <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #3498db',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 15px',
                }} />
                <style>
                    {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
                </style>
                <div style={{ color: '#666', fontSize: '14px' }}>
                    Checking authentication...
                </div>
            </div>
        </div>
    );
}

/**
 * ProtectedRoute component
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string[]} props.allowedRoles - Array of allowed roles
 * @param {string} props.fallbackPath - Path to redirect to (default: '/')
 */
function ProtectedRoute({ 
    children, 
    allowedRoles = [], 
    fallbackPath = '/' 
}) {
    const { isAuthenticated, loading, isInitializing, hasRole, user } = useAuth();
    const location = useLocation();
    
    console.log('[ProtectedRoute] Render - isAuthenticated:', isAuthenticated, 'loading:', loading, 'isInitializing:', isInitializing, 'user:', user, 'allowedRoles:', allowedRoles);

    // CRITICAL: Show loading/spinner while initializing
    // isInitializing = true means we're still checking auth (including refresh attempt)
    // This prevents the brief flash to login page when refresh is in progress
    if (isInitializing) {
        return <LoadingSpinner />;
    }

    // Show loading for non-initializing auth checks (e.g., after logout)
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
            }}>
                <div>Loading...</div>
            </div>
        );
    }

    // Redirect to login if not authenticated (only after initialization is complete)
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // If allowedRoles is specified, check role authorization
    if (allowedRoles.length > 0) {
        console.log('[ProtectedRoute] Checking roles - allowedRoles:', allowedRoles, 'user.role:', user?.role);
        const isAuthorized = hasRole(allowedRoles);
        console.log('[ProtectedRoute] isAuthorized:', isAuthorized);
        
        if (!isAuthorized) {
            // User is authenticated but lacks required role
            // Redirect to fallback path with unauthorized flag
            return <Navigate to={fallbackPath} state={{ unauthorized: true }} replace />;
        }
    }

    // User is authenticated and authorized (or no role restriction)
    return children;
}

/**
 * Higher-order component for wrapping routes with role-based protection
 * 
 * @param {React.ComponentType} Component - Component to wrap
 * @param {string[]} allowedRoles - Roles allowed to access the component
 * @param {string} fallbackPath - Path to redirect unauthorized users
 */
export function withRoleProtection(Component, allowedRoles, fallbackPath = '/') {
    return function ProtectedComponent(props) {
        return (
            <ProtectedRoute allowedRoles={allowedRoles} fallbackPath={fallbackPath}>
                <Component {...props} />
            </ProtectedRoute>
        );
    };
}

export default ProtectedRoute;
